import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Provider } from 'react-redux'
import Quill from 'quill'
import ReactQuill from 'react-quill'
import short from 'shortid'
import { Button } from 'semantic-ui-react'
import { createRoot } from 'react-dom/client'
import ReCAPTCHA from 'react-google-recaptcha'

import environment from 'environment'

import { emptyTextArea } from 'types/editor'

import { store } from 'redux/store'

import ToastService from 'services/toast.service'

import InlineCardPreview from 'components/formElements/richText/InlineCardPreview'
import Toolbar from 'components/formElements/richText/Toolbar'
import ImagePrompt from 'components/formElements/richText/ImagePrompt'

import { isEmpty } from 'utils/Deltas'

import styles from './quill.module.scss'
import { json } from 'stream/consumers'

const Embed = Quill.import('blots/embed')
const QuillLink = Quill.import('formats/link')

const FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'color',
  'background',
  'link',
  'image',
  'video',
  'card-link',
]

const noOp = () => {
  return
}

/**
 * This is a wrapper for @see CardLink that goes into the text field. It manipulates the information in the field
 * in order to display the extra info (ie: the card images)
 */
class InlineCardLink extends Embed {
  static create(value: string): any {
    const node = super.create(value)

    node.setAttribute('name', value)
    node.setAttribute('contenteditable', false)

    const subRoot = createRoot(node)

    subRoot.render(
      <Provider store={store}>
        <InlineCardPreview name={value}>{value}</InlineCardPreview>
      </Provider>,
    )

    return node
  }

  static value(node: any): string {
    return node.getAttribute('name')
  }
}

InlineCardLink.blotName = 'card-link'
InlineCardLink.className = 'card-link'
InlineCardLink.tagName = 'span'

Quill.register({ 'formats/card-link': InlineCardLink })

class OverrideQuillLink extends QuillLink {
  static create(value: string) {
    let node = QuillLink.create(value)
    value = QuillLink.sanitize(value)
    node.setAttribute('href', value)
    if (value.startsWith('https://archidekt.com')) {
      node.removeAttribute('target')
      node.removeAttribute('rel')
    } else {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'nofollow noopener noreferrer')
    }
    return node
  }
}

Quill.register(OverrideQuillLink)

export type Props = {
  value?: string
  onChange?: (updatedText: string) => void
  readOnly?: boolean
  placeholder?: string
  onSubmit?: () => Promise<any> // handle any errors in the parent, otherwise, they'll be eaten
  onCancel?: () => void
  autofocus?: boolean
  captcha?: boolean
  disable?: boolean
  header?: React.ReactNode
}

/**
 * This class displays an comment, fourm, deck description, or any other rich text editors on the site.
 */
const QuillWrapper = ({
  value = emptyTextArea,
  readOnly,
  placeholder,
  onChange,
  onSubmit,
  onCancel,
  autofocus = false,
  captcha = false,
  disable = false,
  header = null,
}: Props) => {
  const parseTextArea = (value: string) => {
    try {
      return JSON.parse(value)
    }
    catch {
      return emptyTextArea
    }
  }
  const quillValue = parseTextArea(value)
  const quillRef = useRef<ReactQuill | undefined>()

  const [captchaAccepted, setCaptchaAccepted] = useState(!captcha || environment.nodeEnv === 'development')
  const [promptOpen, setPromptOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [containerId] = useState(short.generate())

  /**
   * Get's reference to the editor that is on screen.
   */
  const getQuill = () => quillRef.current?.getEditor()

  /**
   * Maintains changes to the edior as new text (or other things) are added to the editor
   */
  const handleChange = (_content: string, _delta: any, _source: any, editor: any) => {
    if (onChange) onChange(JSON.stringify(editor.getContents()))
  }

  const handleSubmit = () => {
    if (!onSubmit) return // not possible, but makes TS happy
    if (isEmpty(value)) return ToastService.create('Cannot submit an empty text area')

    setLoading(true)

    onSubmit().finally(() => setLoading(false)) // handle error in the parent
  }

  const toolbarId = `toolbar-${containerId}`

  const modules = useMemo(
    // Creates module options object for the editor
    () => ({
      toolbar: readOnly
        ? false
        : {
            container: `#${toolbarId}`,
            handlers: {
              image: noOp,
            },
          },
    }),
    [readOnly],
  )

  useEffect(() => {
    const quill = getQuill()

    if (autofocus) quill?.focus()
  }, [])

  return (
    <div>
      {header && <label className={styles.header}>{header}</label>}
      <div className={`${styles.container} ${readOnly ? styles.readOnly : ''}`}>
        {!readOnly && <Toolbar toolbarId={toolbarId} getQuill={getQuill} promptImage={() => setPromptOpen(true)} />}
        <ReactQuill
          theme="snow"
          //@ts-ignore
          ref={quillRef}
          placeholder={placeholder}
          value={quillValue}
          modules={modules}
          readOnly={readOnly}
          onChange={handleChange}
          formats={FORMATS}
          disable={disable}
        />
        <ImagePrompt active={promptOpen} disable={() => setPromptOpen(false)} getQuill={getQuill} />
      </div>
      <div className={styles.buttonContainer}>
        <div>
          {captcha && environment.nodeEnv !== 'development' && (
            <ReCAPTCHA sitekey={environment.reCaptchaSiteKey} onChange={setCaptchaAccepted} />
          )}
        </div>
        <div>
          {onCancel && (
            <Button color="red" onClick={onCancel} size="tiny">
              Cancel
            </Button>
          )}
          {onSubmit && (
            <Button onClick={handleSubmit} disabled={!captchaAccepted} loading={loading} color="green" size="tiny">
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuillWrapper
